// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Exploration Configuration
 * 
 * Contains all settings for planet exploration and system scanning mechanics.
 */

export const explorationConfig = {
  // All available exploration locations
  locations: [
    // LOW RISK LOCATIONS
    {
      id: 'forest',
      name: 'Bioluminescent Forest',
      icon: '🌲',
      description: 'Dense vegetation with glowing flora',
      risk: 'LOW',
      outcomes: [
        { type: 'positive', chance: 0.5, credits: 120, message: 'Harvested valuable bio-samples. +120 credits', icon: '🌿' },
        { type: 'positive', chance: 0.3, credits: 80, message: 'Discovered rare medicinal plants. +80 credits', icon: '🍃' },
        { type: 'negative', chance: 0.1, credits: -50, message: 'Mild toxic spores. Medical treatment. -50 credits', icon: '☠️' },
        { type: 'neutral', chance: 0.1, credits: 0, message: 'Beautiful scenery. Crew morale boosted.', icon: '✨' }
      ]
    },
    {
      id: 'meadow',
      name: 'Crystal Meadow',
      icon: '🌸',
      description: 'Peaceful plains with crystalline formations',
      risk: 'LOW',
      outcomes: [
        { type: 'positive', chance: 0.6, credits: 100, message: 'Collected decorative crystals. +100 credits', icon: '💎' },
        { type: 'positive', chance: 0.2, credits: 150, message: 'Found rare crystal specimens! +150 credits', icon: '✨' },
        { type: 'neutral', chance: 0.2, credits: 0, message: 'Serene landscape. Nothing of value found.', icon: '🌈' }
      ]
    },
    {
      id: 'stream',
      name: 'Mineral Stream',
      icon: '💧',
      description: 'Flowing water with mineral deposits',
      risk: 'LOW',
      outcomes: [
        { type: 'positive', chance: 0.5, credits: 90, message: 'Extracted valuable minerals. +90 credits', icon: '⚗️' },
        { type: 'positive', chance: 0.3, credits: 130, message: 'Discovered rare earth elements. +130 credits', icon: '🔬' },
        { type: 'negative', chance: 0.1, credits: -40, message: 'Equipment water damage. -40 credits', icon: '💦' },
        { type: 'neutral', chance: 0.1, credits: 0, message: 'Refreshing water source. Samples collected.', icon: '🌊' }
      ]
    },
    {
      id: 'garden',
      name: 'Alien Garden',
      icon: '🪴',
      description: 'Cultivated area with exotic plant life',
      risk: 'LOW',
      outcomes: [
        { type: 'positive', chance: 0.5, credits: 110, message: 'Harvested exotic fruits. +110 credits', icon: '🍇' },
        { type: 'positive', chance: 0.3, credits: 85, message: 'Found edible vegetation. +85 credits', icon: '🥬' },
        { type: 'neutral', chance: 0.2, credits: 0, message: 'Peaceful garden. No commercial value.', icon: '🌺' }
      ]
    },
    {
      id: 'beach',
      name: 'Coastal Shore',
      icon: '🏖️',
      description: 'Sandy beaches with tidal pools',
      risk: 'LOW',
      outcomes: [
        { type: 'positive', chance: 0.4, credits: 95, message: 'Collected rare shells and pearls. +95 credits', icon: '🐚' },
        { type: 'positive', chance: 0.4, credits: 120, message: 'Found valuable marine specimens. +120 credits', icon: '🦀' },
        { type: 'neutral', chance: 0.2, credits: 0, message: 'Relaxing shoreline. Nothing valuable.', icon: '🌅' }
      ]
    },

    // MODERATE RISK LOCATIONS
    {
      id: 'cave',
      name: 'Ancient Cave System',
      icon: '🕳️',
      description: 'Deep caverns with unusual energy readings',
      risk: 'MODERATE',
      outcomes: [
        { type: 'positive', chance: 0.4, credits: 200, message: 'Discovered valuable crystals! +200 credits', icon: '💎' },
        { type: 'positive', chance: 0.3, credits: 100, message: 'Found abandoned cargo cache. +100 credits', icon: '📦' },
        { type: 'negative', chance: 0.2, credits: -150, message: 'Cave collapse! Emergency extraction. -150 credits', icon: '⚠️' },
        { type: 'neutral', chance: 0.1, credits: 0, message: 'Interesting geological formations. No resources.', icon: '🪨' }
      ]
    },
    {
      id: 'ice',
      name: 'Frozen Wasteland',
      icon: '❄️',
      description: 'Sub-zero temperatures, possible resources',
      risk: 'MODERATE',
      outcomes: [
        { type: 'positive', chance: 0.4, credits: 180, message: 'Found frozen resource deposits. +180 credits', icon: '🧊' },
        { type: 'positive', chance: 0.3, credits: 120, message: 'Discovered ice crystals with rare elements. +120 credits', icon: '💎' },
        { type: 'negative', chance: 0.2, credits: -120, message: 'Blizzard conditions. Extra power needed. -120 credits', icon: '🌨️' },
        { type: 'neutral', chance: 0.1, credits: 0, message: 'Harsh but stable conditions. No findings.', icon: '🥶' }
      ]
    },
    {
      id: 'canyon',
      name: 'Deep Canyon',
      icon: '🏜️',
      description: 'Massive gorge with exposed mineral layers',
      risk: 'MODERATE',
      outcomes: [
        { type: 'positive', chance: 0.4, credits: 190, message: 'Mined exposed ore deposits. +190 credits', icon: '⛏️' },
        { type: 'positive', chance: 0.3, credits: 140, message: 'Found fossil fuels. +140 credits', icon: '🛢️' },
        { type: 'negative', chance: 0.2, credits: -100, message: 'Rockslide damage. -100 credits', icon: '🪨' },
        { type: 'neutral', chance: 0.1, credits: 0, message: 'Impressive geology. No extractable resources.', icon: '🏔️' }
      ]
    },
    {
      id: 'swamp',
      name: 'Toxic Swamp',
      icon: '🐊',
      description: 'Murky wetlands with chemical compounds',
      risk: 'MODERATE',
      outcomes: [
        { type: 'positive', chance: 0.35, credits: 170, message: 'Extracted valuable chemical compounds. +170 credits', icon: '🧪' },
        { type: 'positive', chance: 0.35, credits: 130, message: 'Found rare amphibian specimens. +130 credits', icon: '🐸' },
        { type: 'negative', chance: 0.25, credits: -130, message: 'Corrosive exposure. Equipment damage. -130 credits', icon: '☣️' },
        { type: 'neutral', chance: 0.05, credits: 0, message: 'Hazardous environment. Quick retreat.', icon: '⚠️' }
      ]
    },
    {
      id: 'geyser',
      name: 'Geyser Field',
      icon: '♨️',
      description: 'Active geothermal vents and hot springs',
      risk: 'MODERATE',
      outcomes: [
        { type: 'positive', chance: 0.4, credits: 160, message: 'Harvested geothermal energy samples. +160 credits', icon: '⚡' },
        { type: 'positive', chance: 0.3, credits: 200, message: 'Found thermal-resistant minerals! +200 credits', icon: '🔥' },
        { type: 'negative', chance: 0.2, credits: -110, message: 'Steam burns. Medical supplies used. -110 credits', icon: '🩹' },
        { type: 'neutral', chance: 0.1, credits: 0, message: 'Spectacular eruptions. Too dangerous to approach.', icon: '💨' }
      ]
    },
    {
      id: 'jungle',
      name: 'Dense Jungle',
      icon: '🌴',
      description: 'Thick vegetation with hidden dangers',
      risk: 'MODERATE',
      outcomes: [
        { type: 'positive', chance: 0.35, credits: 150, message: 'Discovered rare botanical samples. +150 credits', icon: '🌺' },
        { type: 'positive', chance: 0.35, credits: 110, message: 'Found valuable hardwood. +110 credits', icon: '🪵' },
        { type: 'negative', chance: 0.25, credits: -90, message: 'Hostile wildlife encounter. -90 credits', icon: '🐅' },
        { type: 'neutral', chance: 0.05, credits: 0, message: 'Impenetrable undergrowth. Turned back.', icon: '🌿' }
      ]
    },

    // HIGH RISK LOCATIONS
    {
      id: 'ruins',
      name: 'Alien Ruins',
      icon: '🏛️',
      description: 'Remnants of an ancient civilization',
      risk: 'HIGH',
      outcomes: [
        { type: 'positive', chance: 0.3, credits: 300, message: 'Ancient artifacts recovered! +300 credits', icon: '⚡' },
        { type: 'positive', chance: 0.2, credits: 150, message: 'Salvaged advanced technology. +150 credits', icon: '🔧' },
        { type: 'negative', chance: 0.3, credits: -200, message: 'Triggered defense systems! -200 credits', icon: '🚨' },
        { type: 'negative', chance: 0.2, credits: -100, message: 'Structural instability. Quick retreat. -100 credits', icon: '💥' }
      ]
    },
    {
      id: 'ocean',
      name: 'Underwater Trench',
      icon: '🌊',
      description: 'Deep ocean with mysterious signals',
      risk: 'HIGH',
      outcomes: [
        { type: 'positive', chance: 0.35, credits: 250, message: 'Found sunken ship with valuable cargo! +250 credits', icon: '🚢' },
        { type: 'positive', chance: 0.25, credits: 150, message: 'Discovered rare minerals. +150 credits', icon: '💠' },
        { type: 'negative', chance: 0.25, credits: -180, message: 'Pressure damage to shuttle. -180 credits', icon: '💧' },
        { type: 'negative', chance: 0.15, credits: -80, message: 'Hostile sea creatures. Evasive maneuvers. -80 credits', icon: '🦑' }
      ]
    },
    {
      id: 'crashed_ship',
      name: 'Crashed Starship',
      icon: '🛸',
      description: 'Derelict vessel with salvageable tech',
      risk: 'HIGH',
      outcomes: [
        { type: 'positive', chance: 0.35, credits: 280, message: 'Salvaged advanced components! +280 credits', icon: '⚙️' },
        { type: 'positive', chance: 0.25, credits: 200, message: 'Recovered intact cargo. +200 credits', icon: '📦' },
        { type: 'negative', chance: 0.25, credits: -170, message: 'Radiation exposure. Decontamination needed. -170 credits', icon: '☢️' },
        { type: 'negative', chance: 0.15, credits: -120, message: 'Unstable reactor. Emergency evacuation. -120 credits', icon: '⚠️' }
      ]
    },
    {
      id: 'temple',
      name: 'Ancient Temple',
      icon: '⛩️',
      description: 'Sacred structure with mysterious power',
      risk: 'HIGH',
      outcomes: [
        { type: 'positive', chance: 0.3, credits: 320, message: 'Discovered priceless relics! +320 credits', icon: '👑' },
        { type: 'positive', chance: 0.25, credits: 180, message: 'Found ceremonial artifacts. +180 credits', icon: '🗿' },
        { type: 'negative', chance: 0.3, credits: -190, message: 'Cursed artifacts! Systems malfunction. -190 credits', icon: '👻' },
        { type: 'negative', chance: 0.15, credits: -140, message: 'Temple guardians activated. Hasty escape. -140 credits', icon: '⚔️' }
      ]
    },
    {
      id: 'volcano',
      name: 'Active Volcano',
      icon: '🌋',
      description: 'Erupting mountain with lava flows',
      risk: 'HIGH',
      outcomes: [
        { type: 'positive', chance: 0.3, credits: 270, message: 'Harvested volcanic minerals! +270 credits', icon: '💎' },
        { type: 'positive', chance: 0.25, credits: 220, message: 'Collected geothermal energy samples. +220 credits', icon: '⚡' },
        { type: 'negative', chance: 0.3, credits: -210, message: 'Lava flow! Heat damage. -210 credits', icon: '🔥' },
        { type: 'negative', chance: 0.15, credits: -150, message: 'Volcanic gases. Life support strain. -150 credits', icon: '💨' }
      ]
    },
    {
      id: 'desert',
      name: 'Scorching Desert',
      icon: '🏜️',
      description: 'Extreme heat with buried treasures',
      risk: 'HIGH',
      outcomes: [
        { type: 'positive', chance: 0.3, credits: 240, message: 'Unearthed buried cache! +240 credits', icon: '💰' },
        { type: 'positive', chance: 0.3, credits: 190, message: 'Found rare desert minerals. +190 credits', icon: '💎' },
        { type: 'negative', chance: 0.25, credits: -160, message: 'Sandstorm damage. -160 credits', icon: '🌪️' },
        { type: 'negative', chance: 0.15, credits: -110, message: 'Extreme heat. Cooling systems overload. -110 credits', icon: '🔥' }
      ]
    },

    // EXTREME RISK LOCATIONS
    {
      id: 'black_hole',
      name: 'Singularity Edge',
      icon: '⚫',
      description: 'Event horizon of a micro black hole',
      risk: 'EXTREME',
      outcomes: [
        { type: 'positive', chance: 0.2, credits: 500, message: 'Harvested exotic matter! +500 credits', icon: '🌌' },
        { type: 'positive', chance: 0.2, credits: 350, message: 'Collected gravitational energy. +350 credits', icon: '⚡' },
        { type: 'negative', chance: 0.4, credits: -320, message: 'Gravitational shear! Critical damage. -320 credits', icon: '💥' },
        { type: 'negative', chance: 0.2, credits: -220, message: 'Time dilation effects. Systems desync. -220 credits', icon: '⏰' }
      ]
    },
    {
      id: 'plasma_storm',
      name: 'Plasma Storm',
      icon: '⚡',
      description: 'Raging electromagnetic tempest',
      risk: 'EXTREME',
      outcomes: [
        { type: 'positive', chance: 0.25, credits: 400, message: 'Captured plasma energy! +400 credits', icon: '⚡' },
        { type: 'positive', chance: 0.2, credits: 320, message: 'Harvested ionized particles. +320 credits', icon: '💫' },
        { type: 'negative', chance: 0.35, credits: -280, message: 'EMP blast! Electronics fried. -280 credits', icon: '💥' },
        { type: 'negative', chance: 0.2, credits: -190, message: 'Magnetic interference. Navigation failure. -190 credits', icon: '🧭' }
      ]
    },
    {
      id: 'hyperstorm',
      name: 'Hyperstorm Vortex',
      icon: '🌪️',
      description: 'Massive atmospheric disturbance',
      risk: 'EXTREME',
      outcomes: [
        { type: 'positive', chance: 0.2, credits: 360, message: 'Harvested atmospheric energy! +360 credits', icon: '⚡' },
        { type: 'positive', chance: 0.2, credits: 280, message: 'Collected rare gas compounds. +280 credits', icon: '💨' },
        { type: 'negative', chance: 0.4, credits: -290, message: 'Lightning strike! Systems fried. -290 credits', icon: '⚡' },
        { type: 'negative', chance: 0.2, credits: -200, message: 'Extreme turbulence. Structural damage. -200 credits', icon: '🌀' }
      ]
    },
    {
      id: 'crystal_cave',
      name: 'Resonant Crystal Cave',
      icon: '💠',
      description: 'Cavern filled with energy-emitting crystals',
      risk: 'EXTREME',
      outcomes: [
        { type: 'positive', chance: 0.25, credits: 420, message: 'Harvested power crystals! +420 credits', icon: '💎' },
        { type: 'positive', chance: 0.2, credits: 300, message: 'Collected resonant minerals. +300 credits', icon: '✨' },
        { type: 'negative', chance: 0.35, credits: -260, message: 'Sonic resonance! Equipment failure. -260 credits', icon: '🔊' },
        { type: 'negative', chance: 0.2, credits: -180, message: 'Crystal collapse. Emergency extraction. -180 credits', icon: '💥' }
      ]
    },
    {
      id: 'void',
      name: 'Void Rift',
      icon: '🕳️',
      description: 'Tear in spacetime fabric',
      risk: 'EXTREME',
      outcomes: [
        { type: 'positive', chance: 0.15, credits: 550, message: 'Collected void energy! Unprecedented discovery! +550 credits', icon: '🌌' },
        { type: 'positive', chance: 0.2, credits: 380, message: 'Harvested dimensional fragments. +380 credits', icon: '🔮' },
        { type: 'negative', chance: 0.45, credits: -350, message: 'Reality breach! Catastrophic damage. -350 credits', icon: '💀' },
        { type: 'negative', chance: 0.2, credits: -240, message: 'Dimensional instability. Systems corrupted. -240 credits', icon: '⚠️' }
      ]
    }
  ],

  /**
   * Get exploration locations filtered by planet threat level
   * Returns 6 random locations appropriate for the threat level
   */
  getLocationsForThreat: (threatLevel) => {
    const threat = threatLevel.toUpperCase();
    
    // Define risk distribution based on planet threat
    const riskWeights = {
      'LOW': { 'LOW': 0.6, 'MODERATE': 0.3, 'HIGH': 0.1, 'EXTREME': 0 },
      'MODERATE': { 'LOW': 0.2, 'MODERATE': 0.4, 'HIGH': 0.3, 'EXTREME': 0.1 },
      'HIGH': { 'LOW': 0, 'MODERATE': 0.2, 'HIGH': 0.5, 'EXTREME': 0.3 },
      'UNKNOWN': { 'LOW': 0.25, 'MODERATE': 0.25, 'HIGH': 0.25, 'EXTREME': 0.25 }
    };

    const weights = riskWeights[threat] || riskWeights['MODERATE'];
    
    // Build weighted pool of locations
    const weightedPool = [];
    explorationConfig.locations.forEach(location => {
      const weight = weights[location.risk] || 0;
      const count = Math.round(weight * 100); // Convert to integer count
      for (let i = 0; i < count; i++) {
        weightedPool.push(location);
      }
    });
    
    // Shuffle the pool
    for (let i = weightedPool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [weightedPool[i], weightedPool[j]] = [weightedPool[j], weightedPool[i]];
    }
    
    // Select 6 unique locations
    const selected = [];
    const usedIds = new Set();
    
    for (const location of weightedPool) {
      if (!usedIds.has(location.id)) {
        selected.push(location);
        usedIds.add(location.id);
        if (selected.length === 6) break;
      }
    }
    
    // If we don't have 6 unique locations, fill with random ones
    if (selected.length < 6) {
      const remaining = explorationConfig.locations.filter(loc => !usedIds.has(loc.id));
      while (selected.length < 6 && remaining.length > 0) {
        const randomIndex = Math.floor(Math.random() * remaining.length);
        selected.push(remaining[randomIndex]);
        remaining.splice(randomIndex, 1);
      }
    }
    
    return selected;
  },
  // ============================================================================
  // PLANET EXPLORATION
  // ============================================================================
  exploration: {
    // Exploration duration
    explorationDuration: 8000,    // 8 seconds
    
    // Outcome chances
    outcomes: {
      resourcesChance: 0.4,       // 40% find resources
      hazardChance: 0.3,          // 30% encounter hazard
      nothingChance: 0.3          // 30% find nothing
    },
    
    // Resource rewards
    resources: {
      fuelMin: 10,                // Minimum fuel reward (%)
      fuelMax: 25,                // Maximum fuel reward (%)
      creditsMin: 100,
      creditsMax: 500
    },
    
    // Hazard damage
    hazards: {
      minDamage: 5,
      maxDamage: 20,
      systemsAtRisk: ['HULL', 'SHIELDS', 'SENSORS']
    }
  },

  // ============================================================================
  // SCANNING SYSTEM
  // ============================================================================
  scanning: {
    // Scan duration
    scanDuration: 5000,           // 5 seconds
    scanProgressInterval: 80,     // Update every 80ms
    
    // Scan costs
    fuelCost: 5,                  // 5% fuel per scan
    creditsCost: 0,               // Free for now (could add later)
    
    // Base credit rewards (before multipliers)
    baseCreditsMin: 50,
    baseCreditsMax: 100,
    bonusCreditsMin: 50,
    bonusCreditsMax: 100,
    bonusChance: 0.2,             // 20% chance for bonus
    
    // Threat level multipliers
    threatMultipliers: {
      SAFE: 0.5,                  // 50% rewards (safe = less valuable)
      LOW: 0.75,                  // 75% rewards
      MODERATE: 1.0,              // 100% rewards (baseline)
      HIGH: 1.5,                  // 150% rewards
      CRITICAL: 2.0,              // 200% rewards
      UNKNOWN: 1.25               // 125% rewards (mystery bonus)
    },
    
    // Distance bonus (per 100 LY from Sol)
    distanceBonusPerHundredLY: 0.1, // +10% per 100 LY
    maxDistanceBonus: 1.0,        // Cap at +100%
    
    // Discovery chances
    discoveries: {
      minResources: 1,
      maxResources: 3,
      anomalyChance: 0.6,         // 60% chance
      hiddenRouteChance: 0.3,     // 30% chance
      minPlanets: 2,
      maxPlanets: 5
    },
    
    // Life sign detection
    lifeSigns: {
      intelligentChance: 0.3,     // 30% chance
      primitiveChance: 0.3        // 30% chance (60% total)
    },
    
    // Re-scan settings
    allowRescan: true,
    rescanRewardMultiplier: 0.5,  // 50% rewards on re-scan
    rescanCooldown: 0,            // No cooldown (can re-scan immediately)
    
    // Sensor damage penalty
    sensorDamagePenalty: {
      enabled: true,
      minorThreshold: 50,         // Below 50% sensors
      criticalThreshold: 25,      // Below 25% sensors
      minorPenalty: 0.75,         // 75% rewards
      criticalPenalty: 0.5        // 50% rewards
    },
    
    // Discovery effects (gameplay benefits)
    discoveryEffects: {
      'Dilithium Crystals': {
        type: 'fuelEfficiency',
        value: 0.1,               // 10% less fuel on next warp
        duration: 1               // Lasts for 1 warp
      },
      'Antimatter Deposits': {
        type: 'fuel',
        minValue: 10,
        maxValue: 20              // 10-20% fuel bonus
      },
      'Rare Minerals': {
        type: 'credits',
        minValue: 100,
        maxValue: 200             // 100-200 credits bonus
      },
      'Exotic Matter': {
        type: 'credits',
        minValue: 150,
        maxValue: 300             // 150-300 credits bonus
      },
      'Ancient Artifacts': {
        type: 'credits',
        minValue: 200,
        maxValue: 400             // 200-400 credits bonus (rare)
      }
    }
  }
};
